import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { makeStyles } from '@material-ui/core/styles';
import FolderIcon from '@mui/icons-material/Folder';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { UIFrontendProjectSummary } from '../../../../../../../api/teamMetricsApi';

type SortKey = 'name' | 'size' | 'coverage';
type SortDirection = 'asc' | 'desc';

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(4),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  legendText: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#F7FAFC',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
  },
  sortLabel: {
    '&.MuiTableSortLabel-root': {
      color: '#8E9299',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    '&.MuiTableSortLabel-root:hover': {
      color: '#4A5568',
    },
    '&.Mui-active': {
      color: '#1A1A1A',
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#8E9299',
      fontSize: 14,
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#1A1A1A',
    },
  },
  tableRow: {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
    '&:hover $actionButton': {
      opacity: 1,
    },
  },
  excludedRow: {
    backgroundColor: '#EDF2F7',
    borderLeft: '3px solid #A0AEC0',
    '&:hover': {
      backgroundColor: '#E2E8F0',
    },
    '&:hover $actionButton': {
      opacity: 1,
    },
    '& $projectIcon': {
      color: '#A0AEC0',
    },
  },
  excludedText: {
    fontFamily: 'monospace',
    fontSize: 14,
    color: '#A0AEC0',
  },
  excludedBadge: {
    backgroundColor: '#EDF2F7',
    color: '#718096',
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    marginLeft: 8,
    textTransform: 'uppercase',
    cursor: 'help',
  },
  actionButton: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  actionButtonVisible: {
    opacity: 1,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  toggleButtonGroup: {
    '& .MuiToggleButton-root': {
      padding: '4px 12px',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'none',
      border: '1px solid #E2E8F0',
      color: '#4A5568',
      '&.Mui-selected': {
        backgroundColor: '#EDF2F7',
        color: '#1A1A1A',
        '&:hover': {
          backgroundColor: '#E2E8F0',
        },
      },
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
    },
  },
  filterCount: {
    fontSize: 12,
    color: '#8E9299',
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
  },
  projectCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  projectIcon: {
    fontSize: 16,
    color: '#4A5568',
  },
  projectName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1A1A1A',
    fontFamily: 'monospace',
  },
  monoText: {
    fontFamily: 'monospace',
    fontSize: 14,
    color: '#4A5568',
  },
  coverageStackContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  coverageRowItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'help',
  },
  coverageTypeLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    minWidth: 36,
  },
  coverageTypeLabelUnit: {
    color: '#3182CE',
  },
  coverageTypeLabelComponent: {
    color: '#38A169',
  },
  coverageTypeLabelExcluded: {
    color: '#A0AEC0',
  },
  coveragePct: {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'monospace',
    color: '#1A1A1A',
    minWidth: 45,
  },
  progressBarContainer: {
    width: 80,
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.2s ease',
  },
  progressBarUnit: {
    backgroundColor: '#3182CE',
  },
  progressBarComponent: {
    backgroundColor: '#38A169',
  },
  progressBarExcluded: {
    backgroundColor: '#A0AEC0',
  },
  tooltipContent: {
    padding: 4,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 2,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  tooltipLabel: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  tooltipValue: {
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'monospace',
  },
  teamChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  teamChip: {
    fontSize: 11,
    fontWeight: 500,
    color: '#4A5568',
    backgroundColor: '#EDF2F7',
    padding: '3px 8px',
    borderRadius: 4,
  },
  sizeCell: {
    paddingRight: 48,
  },
}));

interface FrontendProjectTableProps {
  projectSummaries: UIFrontendProjectSummary[];
  onExcludeProject?: (projectName: string) => void;
  onIncludeProject?: (projectName: string) => Promise<void>;
  isExcluding?: boolean;
}

const getUnitStatements = (project: UIFrontendProjectSummary): number | null => {
  const unitCoverage = project.coverageByType.find((c) => c.testType === 'unit');
  const total = unitCoverage?.metrics.statements.total ?? null;
  return total === 0 ? null : total;
};

export const FrontendProjectTable: React.FC<FrontendProjectTableProps> = ({
  projectSummaries,
  onExcludeProject,
  onIncludeProject,
  isExcluding = false,
}) => {
  const classes = useStyles();
  const [sortKey, setSortKey] = useState<SortKey | null>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filter, setFilter] = useState<'all' | 'excluded'>('all');

  const normalizedData = useMemo(() => {
    return projectSummaries.map((project) => {
      const unitCoverage = project.coverageByType.find((c) => c.testType === 'unit');
      const unitPct = unitCoverage?.metrics.statements.pct ?? null;
      const unitStatements = getUnitStatements(project);

      return {
        ...project,
        unitPct,
        unitStatements,
      };
    });
  }, [projectSummaries]);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: 'all' | 'excluded' | null,
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const excludedCount = useMemo(() => normalizedData.filter((p) => p.isExcluded).length, [normalizedData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortKey(null);
        setSortDirection('desc');
      }
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedProjects = useMemo(() => {
    if (!sortKey) {
      return normalizedData;
    }

    return [...normalizedData].sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'name':
          comparison = a.projectName.localeCompare(b.projectName);
          break;
        case 'size':
          if (a.unitStatements === null && b.unitStatements === null) comparison = 0;
          else if (a.unitStatements === null) comparison = -1;
          else if (b.unitStatements === null) comparison = 1;
          else comparison = a.unitStatements - b.unitStatements;
          break;
        case 'coverage':
          if (a.unitPct === null && b.unitPct === null) comparison = 0;
          else if (a.unitPct === null) comparison = -1;
          else if (b.unitPct === null) comparison = 1;
          else comparison = a.unitPct - b.unitPct;
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [normalizedData, sortKey, sortDirection]);

  const filteredProjects = useMemo(() => {
    if (filter === 'excluded') {
      return sortedProjects.filter((p) => p.isExcluded);
    }
    return sortedProjects;
  }, [sortedProjects, filter]);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Project Breakdown</Typography>
        <Box className={classes.legend}>
          <Box className={classes.legendItem}>
            <Box className={classes.legendDot} style={{ backgroundColor: '#3182CE' }} />
            <Typography className={classes.legendText}>Unit</Typography>
          </Box>
          <Box className={classes.legendItem}>
            <Box className={classes.legendDot} style={{ backgroundColor: '#38A169' }} />
            <Typography className={classes.legendText}>Component</Typography>
          </Box>
          <Box className={classes.legendItem}>
            <Box
              className={classes.legendDot}
              style={{
                backgroundColor: '#A0AEC0',
                background:
                  'repeating-linear-gradient(45deg, #A0AEC0, #A0AEC0 2px, #CBD5E0 2px, #CBD5E0 4px)',
              }}
            />
            <Typography className={classes.legendText}>Excluded</Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.filterRow}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          className={classes.toggleButtonGroup}
        >
          <ToggleButton value="all">Show All ({normalizedData.length})</ToggleButton>
          <ToggleButton value="excluded" disabled={excludedCount === 0}>
            Excluded Only ({excludedCount})
          </ToggleButton>
        </ToggleButtonGroup>
        {filter === 'excluded' && excludedCount > 0 && (
          <Typography className={classes.filterCount}>
            Showing {excludedCount} excluded {excludedCount === 1 ? 'project' : 'projects'}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.headerCell}>
                <TableSortLabel
                  active={sortKey === 'name'}
                  direction={sortKey === 'name' ? sortDirection : 'desc'}
                  onClick={() => handleSort('name')}
                  className={classes.sortLabel}
                >
                  Project
                </TableSortLabel>
              </TableCell>
              <TableCell className={`${classes.headerCell} ${classes.sizeCell}`} align="right">
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                  <Tooltip title="Total statements from unit test coverage" placement="top">
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#A0AEC0', cursor: 'help' }} />
                  </Tooltip>
                  <TableSortLabel
                    active={sortKey === 'size'}
                    direction={sortKey === 'size' ? sortDirection : 'desc'}
                    onClick={() => handleSort('size')}
                    className={classes.sortLabel}
                  >
                    Size
                  </TableSortLabel>
                </Box>
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 180 }}>
                <TableSortLabel
                  active={sortKey === 'coverage'}
                  direction={sortKey === 'coverage' ? sortDirection : 'desc'}
                  onClick={() => handleSort('coverage')}
                  className={classes.sortLabel}
                >
                  Test Coverage
                </TableSortLabel>
              </TableCell>
              {(onExcludeProject || onIncludeProject) && (
                <TableCell className={classes.headerCell} width={40} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => (
              <TableRow
                key={project.projectName}
                className={`${classes.tableRow} ${project.isExcluded ? classes.excludedRow : ''}`}
              >
                <TableCell className={classes.cell}>
                  <Box>
                    <Box className={classes.projectCell}>
                      <FolderIcon className={classes.projectIcon} />
                      <Typography className={project.isExcluded ? classes.excludedText : classes.projectName}>
                        {project.projectName}
                      </Typography>
                      {project.isExcluded && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                Excluded from calculations
                              </Typography>
                              {project.exclusionReason && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  Reason: {project.exclusionReason}
                                </Typography>
                              )}
                              {project.excludedByEmail && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  Excluded by: {project.excludedByEmail}
                                </Typography>
                              )}
                              {project.excludedAt && (
                                <Typography variant="body2">
                                  Date: {new Date(project.excludedAt).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <span className={classes.excludedBadge}>Excluded</span>
                        </Tooltip>
                      )}
                    </Box>
                    {project.githubTeamOwners && project.githubTeamOwners.length > 0 && (
                      <Box className={classes.teamChipsContainer}>
                        {project.githubTeamOwners.map((team) => (
                          <span key={team} className={classes.teamChip}>
                            {team}
                          </span>
                        ))}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.sizeCell}`} align="right">
                  {project.unitStatements === null ? (
                    <Tooltip
                      title="If no tests are configured for the project, we don't know the statement counts"
                      arrow
                      placement="top"
                    >
                      <Typography className={project.isExcluded ? classes.excludedText : classes.monoText} style={{ cursor: 'help' }}>
                        N/A
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography className={project.isExcluded ? classes.excludedText : classes.monoText}>
                      {project.unitStatements.toLocaleString()}
                    </Typography>
                  )}
                </TableCell>
                <TableCell className={classes.cell}>
                  {(() => {
                    const coverageWithData = project.coverageByType.filter(
                      (c) => c.metrics.statements.pct !== null
                    );
                    if (coverageWithData.length === 0) {
                      return (
                        <Typography className={project.isExcluded ? classes.excludedText : classes.monoText}>
                          None
                        </Typography>
                      );
                    }
                    return (
                      <Box className={classes.coverageStackContainer}>
                        {coverageWithData.map((coverage) => {
                          const pct = coverage.metrics.statements.pct!;
                          const label = coverage.testType === 'unit' ? 'Unit' :
                                       coverage.testType === 'component' ? 'Comp' :
                                       coverage.testType.charAt(0).toUpperCase() + coverage.testType.slice(1);
                          const labelClass = coverage.testType === 'unit' ? classes.coverageTypeLabelUnit :
                                            coverage.testType === 'component' ? classes.coverageTypeLabelComponent :
                                            classes.coverageTypeLabelUnit;
                          return (
                            <Tooltip
                              key={coverage.testType}
                              title={
                                <Box className={classes.tooltipContent}>
                                  <Box className={classes.tooltipRow}>
                                    <Typography className={classes.tooltipLabel}>Covered:</Typography>
                                    <Typography className={classes.tooltipValue}>
                                      {(coverage.metrics.statements.covered ?? 0).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  <Box className={classes.tooltipRow}>
                                    <Typography className={classes.tooltipLabel}>Total:</Typography>
                                    <Typography className={classes.tooltipValue}>
                                      {(coverage.metrics.statements.total ?? 0).toLocaleString()}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Box className={classes.coverageRowItem}>
                                <Typography className={`${classes.coverageTypeLabel} ${project.isExcluded ? classes.coverageTypeLabelExcluded : labelClass}`}>
                                  {label}
                                </Typography>
                                <Box className={classes.progressBarContainer}>
                                  <Box
                                    className={`${classes.progressBarFill} ${
                                      project.isExcluded ? classes.progressBarExcluded :
                                      coverage.testType === 'unit' ? classes.progressBarUnit :
                                      coverage.testType === 'component' ? classes.progressBarComponent :
                                      classes.progressBarUnit
                                    }`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </Box>
                                <Typography className={project.isExcluded ? classes.excludedText : classes.coveragePct}>
                                  {pct.toFixed(1)}%
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </TableCell>
                {(onExcludeProject || onIncludeProject) && (
                  <TableCell className={classes.cell} align="right">
                    {project.isExcluded && onIncludeProject && (
                      <Tooltip title="Include in calculations">
                        <IconButton
                          className={`${classes.actionButton} ${classes.actionButtonVisible}`}
                          onClick={() => onIncludeProject(project.projectName)}
                          size="small"
                          disabled={isExcluding}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!project.isExcluded && onExcludeProject && (
                      <Tooltip title="Exclude from calculations">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => onExcludeProject(project.projectName)}
                          size="small"
                          disabled={isExcluding}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FrontendProjectTable;
